import * as React from 'react';
import { Icon } from 'sos-common/js/components/Icon/icon';
import thousands from 'thousands';
import { TextInput } from 'sos-common/js/components/FormControls/TextInput';
import { TextInputForPrint } from 'sos-common/js/components/FormControls/TextInputForPrint';
import { Field } from '../../StatsbidragGlobals/statsbidrag.types';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import {
  calculationComponentProps,
  calculationComponentState,
  calculationComponentModel,
  calculationComponentModel2,
  ColumnTypeGroup,
  ColumnValue,
  ColumnValuesSection,
} from '../calculationComponents.types';
import {
  serializeComponent,
  updateField,
  getAsNumber,
  getInitialFieldsFromJsonData,
  valueObject,
  uuidv4,
  getSerializedXmlInputComponent,
  IJsonDataStuctures,
} from '../calculationComponents.helpers';
import '../AutoSum/_autosum.scss';
import EventService from '../calculationComponents.eventservice';
import ValidationService from '../../../services/validation.service';
import { InputError } from 'sos-common/js/components/InputError/InputError';
import { ALL_MANDATORY } from '../calculationComponents.errorMessages';
import { hashCode } from 'sos-common/js/utils/hash';

export interface AssociationsProps extends calculationComponentProps {
  columnOneHeading: string;
  columnOnePlaceholder: string;
  columnTwoHeading: string;
  summaryCellId: string;
  hideAddMore: boolean;
  addMoreText: string;
  preDefinedRows: DynamicCell[];
  flatRateOne: number;
  flatRateTwo: number;
  preambleId: string;
}

export interface AssociationsState extends calculationComponentState {
  fields: Field[][];
  sum: number;
  jsonXmlInput: string; //NOTE: can be moved to calculationComponentState once support exists in all kalkylkomponenter.
}

interface DynamicCell {
  Label: string;
  CellId: string;
}

export class Associations extends React.Component<AssociationsProps, AssociationsState> {
  private _fieldCount: number;
  private _sumLabel: string = `Summa föreningsbidrag`;

  constructor(props: AssociationsProps) {
    super(props);
    this._fieldCount = 0;

    let initialFields: Field[][] = [];
    if (props.preDefinedRows) {
      props.preDefinedRows.forEach((obj) => {
        initialFields.push(this.getFieldRow(obj));
      });
    } else {
      initialFields = [this.getFieldRow()];
    }

    let isValid = true;

    if (props.formElementJson) {
      let calculationComponentModel = JSON.parse(props.formElementJson) as calculationComponentModel;
      initialFields = getInitialFieldsFromJsonData(
        calculationComponentModel,
        initialFields,
        this.getFieldRow,
        props.preDefinedRows
      );

      let validation = ValidationService.validateComponent(initialFields, props.formElementName);
      isValid = validation.componentIsValid;
      initialFields = validation.fieldsClone;
    }

    this.state = {
      fields: initialFields,
      sum: 0,
      jsonFields: '',
      isValid,
      isTouched: false,
      jsonXmlInput: '',
    };
  }

  componentDidMount() {
    this.summarizeFields();
    EventService.subscribe('form-is-valid', (e: CustomEvent) => {
      this.setState({
        isTouched: true,
      });
    });
  }

  getSerializedComponent = (fields: Field[][]) => {
    const { columnOneHeading, columnTwoHeading, formElementHeading } = this.props;
    let values = [];
    fields.forEach((field) => {
      values.push([valueObject(field[0].value, true), valueObject(field[1].value, true)]);
    });
    if (values.length > 0) {
      return serializeComponent(
        [columnOneHeading, columnTwoHeading],
        values,
        ['Summa föreningsbidrag', thousands(this.state.sum, ' ')],
        formElementHeading
      );
    }
    return '';
  };

  getFieldRow = (preDefinedObj?: DynamicCell) => {
    const col1value = preDefinedObj ? preDefinedObj.Label : '';
    const id1 = `autosumfield__${this._fieldCount++}`;
    const id2 = preDefinedObj ? preDefinedObj.CellId : `autosumfield__${this._fieldCount++}`;

    return [
      {
        id: id1,
        name: id1,
        value: col1value,
        placeholder: this.props.columnOnePlaceholder,
        isValid: true,
        mandatory: true,
        isReadOnly: preDefinedObj !== undefined,
        isTouched: false,
        errorMessage: 'Du måste ange en beskrivning',
      },
      {
        id: id2,
        name: id2,
        value: '0',
        placeholder: 'Ange antal',
        isValid: true,
        mandatory: true,
        isTouched: false,
        errorMessage: 'Du måste ange ett antal',
      },
    ] as Field[];
  };

  summarizeFields = () => {
    const { fields } = this.state;
    const { formElementName, formElementHeading } = this.props;

    let sum = 0;
    let val1 = getAsNumber(fields[0][1].value);
    let val2 = getAsNumber(fields[1][1].value);

    sum = val1 * this.props.flatRateOne + val2 * this.props.flatRateTwo;

    this.setState(
      {
        sum,
      },
      () => {
        EventService.fire(`cell-updated:${this.props.summaryCellId}`, { detail: this.state.sum });
        this.setState({
          jsonFields: this.getSerializedComponent(this.state.fields),
          jsonXmlInput: getSerializedXmlInputComponent(
            this.state.fields,
            calculationComponentModel2.Create(formElementName, formElementHeading, 'Föreningar'),
            this.jsonSeriliazetoMainContentForXmlInput
          ),
        });
      }
    );
  };

  jsonSeriliazetoMainContentForXmlInput = (fields: Field[][], data: IJsonDataStuctures) => {
    const resultArray = {
      static: [] as ColumnValue[],
      dynamic: [] as ColumnValuesSection[],
      sum: [] as ColumnValue[],
    };

    fields.forEach((rowfields) => {
      if (rowfields.length !== 2) throw 'For this component AutoSum, there should only be two fields per row';
      const headerValue = rowfields[0].value;
      const value = rowfields[1].value;
      const isStaticHeaderRow = rowfields[1].id.startsWith('associationscell'); //NOTE: this is different for different components.
      if (isStaticHeaderRow) {
        resultArray.static.push({ headerValue, value });
      } else {
        const headerColumnValue = { value: headerValue } as ColumnValue;
        const valueColumnValue = { value } as ColumnValue;
        const columnValuesSection = { columnValues: [headerColumnValue, valueColumnValue] } as ColumnValuesSection;
        resultArray.dynamic.push(columnValuesSection);
      }
    });
    resultArray.sum.push({ headerValue: this._sumLabel, value: thousands(this.state.sum, ' ') });

    const columnTypeGroup = {
      static: { columnValues: resultArray.static } as ColumnTypeGroup,
      dynamic: { columnValuesSections: resultArray.dynamic } as ColumnTypeGroup,
      sum: { isSum: true, columnValues: resultArray.sum } as ColumnTypeGroup,
    };
    data.columnTypeGroups.push(columnTypeGroup.static);
    data.columnTypeGroups.push(columnTypeGroup.dynamic);
    data.columnTypeGroups.push(columnTypeGroup.sum);
  };

  jsonSerializetoSummaForXmlInput = (): ColumnTypeGroup => {
    const sumColumnType = { isSum: true, columnValues: [] } as ColumnTypeGroup;
    sumColumnType.columnValues.push({ headerValue: this._sumLabel, value: thousands(this.state.sum, ' ') });
    return sumColumnType;
  };

  validateMe = () => {
    let validation = ValidationService.validateComponent(this.state.fields, this.props.formElementName);
    let isValid = validation.componentIsValid;
    let fields = validation.fieldsClone;
    this.setState(
      {
        fields,
        isValid,
      },
      () => {
        ValidationService.validateStepViaElementName(this.props.formElementName);
      }
    );
  };

  setValue = (field: FormField<any>) => {
    const fieldsClone = this.state.fields.slice();
    const fieldToUpdate = updateField(field, fieldsClone);

    this.setState(
      {
        fields: fieldsClone,
        //isTouched: true
      },
      () => {
        this.validateMe();
        EventService.fire(`cell-updated:${fieldToUpdate.id}`, { detail: fieldToUpdate.value });
        this.summarizeFields();
      }
    );
  };

  renderRows = (row: Field[], index: number) => {
    let readOnlyClass = row[0].isReadOnly ? 'stb-form-readonly' : '';
    let isLastRow = index === this.state.fields.length - 1;
    readOnlyClass += isLastRow ? ' last' : '';
    this.setAriaErrorObjects(row);

    return (
      <React.Fragment key={`${this.props.summaryCellId}_autosumrow_${index}`}>
        <div className="autosum__row" role="row">
          <div className={`autosum__col1 ${readOnlyClass}`} role="cell">
            <TextInputForPrint
              id={row[0].id}
              classNames={this.shouldShowError(row[0]) ? 'has-error' : ''}
              type="text"
              placeholder={row[0].placeholder}
              value={row[0].value}
              onChange={this.setValue}
              fieldName={row[0].name}
              disabled={row[0].isReadOnly}
              isRequired={!row[0].isReadOnly}
              onBlur={this.onBlur}
              errorAriaAttributes={row[0].errorAriaAttributes}
            />
          </div>

          <div className="autosum__col2" role="cell">
            <TextInputForPrint
              id={row[1].id}
              classNames={`right ${this.shouldShowError(row[1]) ? 'has-error' : ''}`}
              type="text"
              placeholder={row[1].placeholder}
              value={row[1].value}
              onChange={this.setValue}
              fieldName={row[1].name}
              actAsNumber={true}
              isRequired={true}
              onBlur={this.onBlur}
              errorAriaAttributes={row[1].errorAriaAttributes}
            />
          </div>
        </div>
        {row.map((field) => {
          if (this.shouldShowError(field) && field.errorAriaAttributes) {
            return (
              <InputError
                key={`${field.id}_cError`}
                id={field.errorAriaAttributes['aria-describedby']}
                errorText={field.errorMessage}
              />
            );
          }
        })}
      </React.Fragment>
    );
  };

  setAriaErrorObjects = (fieldRow: Field[]) => {
    fieldRow.forEach((field) => {
      field.errorAriaAttributes = null;
      if (this.shouldShowError(field)) {
        field.errorAriaAttributes = { 'aria-invalid': true, 'aria-describedby': uuidv4() };
      }
    });
  };

  shouldShowError = (field: Field) => {
    return (this.state.isTouched || field.isTouched) && !field.isValid;
  };

  onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { fields } = this.state;
    const clone = fields.slice();
    let targetField: Field = null;
    clone.forEach((field) => {
      let foundField = field.find((f) => f.id === e.target.id);
      if (foundField) targetField = foundField;
    });
    if (targetField) {
      targetField.isTouched = true;
      this.setState({ fields: clone });
    }
  };

  onAddMore = (e: React.MouseEvent) => {
    e.preventDefault();
    const fieldsClone = this.state.fields.slice();
    const addedRow = this.getFieldRow();
    fieldsClone.push(addedRow);
    this.setState(
      {
        fields: fieldsClone,
        isTouched: true,
      },
      () => {
        const fieldToFocus = document.getElementById(addedRow[0].id);
        if (fieldToFocus) fieldToFocus.focus();
        this.validateMe();
        this.summarizeFields();
      }
    );
  };

  public render() {
    const {
      columnOneHeading,
      columnTwoHeading,
      summaryCellId,
      hideAddMore,
      addMoreText,
      formElementHeading,
      preambleId,
    } = this.props;
    const { fields, sum } = this.state;
    return (
      <div
        className="cc-component"
        role="table"
        aria-label={`${formElementHeading}`}
        aria-describedby={`${preambleId}`}>
        <div role="rowgroup">
          <div className="autosum__row" role="row">
            <div className="autosum__col1" role="columnheader">
              <p className="like-a-label">{columnOneHeading}</p>
            </div>

            <div className="autosum__col2" role="columnheader">
              <p className="like-a-label">{columnTwoHeading}</p>
            </div>
          </div>
        </div>

        <div role="rowgroup">{fields && fields.map(this.renderRows)}</div>

        {!hideAddMore && (
          <div className="autosum__btnwrap">
            <button type="button" className="autosum__add" onClick={this.onAddMore}>
              {addMoreText} <Icon iconType="baseline-add_circle_outline-24px" />
            </button>
          </div>
        )}

        <div className="autosum__summary" role="row">
          <div className="autosum__col1" role="cell">
            <span className="like-a-label">{this._sumLabel}</span>
          </div>
          <div className="autosum__col2" role="cell">
            <input
              className="like-a-label autosum__sumfield"
              id={summaryCellId}
              readOnly={true}
              type="text"
              value={thousands(sum, ' ')}
            />
          </div>
        </div>
        <input
          data-isvalid={this.state.isValid}
          type="hidden"
          name={this.props.formElementName}
          id={this.props.formElementName}
          value={this.state.jsonFields}
        />
        <input
          data-isvalid={this.state.isValid}
          type="hidden"
          name={'__XmlInput' + this.props.formElementName}
          id={'__XmlInput' + this.props.formElementName}
          value={this.state.jsonXmlInput}
        />
      </div>
    );
  }
}
